import React, { Fragment, useRef, useState } from "react";
import styled from "styled-components";
import Colors from "../../styles/Colors";
import { H4 } from "../../styles/Fonts";
import Button from "../../components/Button";
// import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTruncNum } from "../../utils/common";
import {
  depositIntoFarm,
  doClose,
  doRollover,
  registerDepositIntoFarm,
  registerWithdrawFromFarm,
  withdrawFromFarm,
} from "../../utils/api/autoFarmApi";
import useTokenBalance from "../../hooks/useTokenBalance";
import { FarmProps } from "../../utils/constant/farm";
import { useWeb3React } from "@web3-react/core";

type actionTypeProps = "Deposit" | "Withdraw";

type Props = {
  updateAccountFarmStatus: () => void;
  positionValue: number;
  canWithdrawAmount: number;
  registeredDepositAmount: number;
  registeredWithdrawAmount: number;
  updateAutoVaultState: () => void;
  vaultState: number;
  farm: FarmProps;
};

const ActionCol: React.FC<Props> = (props) => {
  const {
    updateAccountFarmStatus,
    positionValue,
    canWithdrawAmount,
    registeredDepositAmount,
    registeredWithdrawAmount,
    updateAutoVaultState,
    vaultState,
    farm,
  } = props;

  const { account, chainId, library } = useWeb3React();
  const { tokenBalance: usdcBalance, updateTokenBalance: updateUsdcBalance } =
    useTokenBalance({
      tokenAddress: farm.tokenAddress,
      tokenDecimal: farm.tokenDecimal,
    });

  const [actionType, setActionType] = useState<actionTypeProps>("Deposit");
  const [amount, setAmount] = useState<number>(0);
  const [isDepositing, setIsDepositing] = useState<boolean>(false);
  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  const handleSwitch = (type: actionTypeProps) => {
    setActionType(type);
    initSetting();
  };

  const updateStates = (_account: string) => {
    updateAccountFarmStatus();
    updateAutoVaultState();

    if (!_account) return;
    updateUsdcBalance();
  };

  const initSetting = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setAmount(0);
  };

  const handleDeposit = async () => {
    if (!account || !amount || !chainId) return;

    setIsDepositing(true);

    if (vaultState === 0) {
      await registerDepositIntoFarm(
        account,
        farm.farmAddress,
        amount,
        farm.tokenAddress,
        farm.tokenDecimal,
        farm.tokenSymbol,
        chainId,
        library
      );
    } else if (vaultState === 1) {
      await depositIntoFarm(
        account,
        farm.farmAddress,
        amount,
        farm.tokenAddress,
        farm.tokenDecimal,
        farm.tokenSymbol,
        chainId,
        library
      );
    }

    setIsDepositing(false);
    initSetting();

    updateStates(account);
  };

  const handleWithdraw = async () => {
    if (account && amount && chainId) {
      setIsWithdrawing(true);

      if (vaultState === 0) {
        await registerWithdrawFromFarm(
          account,
          farm.farmAddress,
          amount,
          farm.tokenDecimal,
          farm.tokenSymbol,
          chainId,
          library
        );
      } else if (vaultState === 1) {
        await withdrawFromFarm(
          account,
          farm.farmAddress,
          amount,
          farm.tokenDecimal,
          farm.tokenSymbol,
          chainId,
          library
        );
      }

      setIsWithdrawing(false);
      initSetting();

      updateStates(account);
    }
  };

  return (
    <OuterContainer>
      <PositionContainer>
        <PositionLabel>Your Position</PositionLabel>
        <PositionValue>
          {account ? `${getTruncNum(positionValue, 6)} USDC` : "- USDC"}
        </PositionValue>
      </PositionContainer>
      <Container>
        <TabsContainer>
          <Tab
            isSelect={actionType === "Deposit"}
            onClick={() => handleSwitch("Deposit")}
          >
            Deposit
          </Tab>
          <Tab
            isSelect={actionType !== "Deposit"}
            onClick={() => handleSwitch("Withdraw")}
          >
            Withdraw
          </Tab>
        </TabsContainer>
        <MainContainer>
          <InputContainer ref={formRef}>
            <LabelContainer>
              <Label>Amount</Label>
            </LabelContainer>
            <Input
              type="number"
              min="0"
              placeholder="100"
              onChange={(e) => setAmount(+e.target.value)}
            />
            <Unit>USDC</Unit>
          </InputContainer>
          {actionType === "Deposit" ? (
            <Fragment>
              {!!registeredDepositAmount && (
                <BalanceRow>
                  <BLabel>Registered Deposit Amount</BLabel>
                  <BValue>
                    {getTruncNum(registeredDepositAmount, 6)} USDC
                  </BValue>
                </BalanceRow>
              )}
              <BalanceRow>
                <BLabel>Wallet Balance</BLabel>
                <BValue>
                  {account ? getTruncNum(usdcBalance, 6) : "-"} USDC
                </BValue>
              </BalanceRow>
            </Fragment>
          ) : (
            <Fragment>
              <BalanceRow>
                <BLabel>Can Withdraw Amount</BLabel>
                <BValue>{`${
                  account ? getTruncNum(canWithdrawAmount, 6) : "-"
                } USDC`}</BValue>
              </BalanceRow>
              {!!registeredWithdrawAmount && (
                <BalanceRow>
                  <BLabel>Registered Withdraw Amount</BLabel>
                  <BValue>{`${getTruncNum(
                    registeredWithdrawAmount,
                    6
                  )} USDC`}</BValue>
                </BalanceRow>
              )}
            </Fragment>
          )}
          <BtnContainer>
            {actionType === "Deposit" ? (
              <Fragment>
                <Button
                  btnText={vaultState === 0 ? "Register Deposit" : "Deposit"}
                  btnType="primary"
                  onClick={handleDeposit}
                  disabled={!(account && amount && !isDepositing)}
                  isLoading={isDepositing}
                />
              </Fragment>
            ) : (
              <Fragment>
                <Button
                  btnText={vaultState === 0 ? "Register Withdraw" : "Withdraw"}
                  btnType="primary"
                  onClick={handleWithdraw}
                  disabled={!(account && amount && !isWithdrawing)}
                  isLoading={isWithdrawing}
                />
              </Fragment>
            )}
          </BtnContainer>
          <BtnContainer>
            <Button
              btnText={"Rollover"}
              btnType="primary"
              onClick={() => doRollover(farm.farmAddress, farm.deployedChainId)}
              disabled={false}
              isLoading={false}
            />
            <Button
              btnText={"Close"}
              btnType="primary"
              onClick={() => doClose(farm.farmAddress, farm.deployedChainId)}
              disabled={false}
              isLoading={false}
            />
          </BtnContainer>
        </MainContainer>
      </Container>
    </OuterContainer>
  );
};

export default ActionCol;

const OuterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Container = styled.div`
  width: 100%;
  background-color: ${Colors.backgroundGray2};
  border-radius: 8px;
`;

const PositionContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background-color: ${Colors.backgroundGray4};
`;

const PositionLabel = styled(H4)`
  color: ${Colors.ivory};
  font-size: 14px;
  line-height: 24px;
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 22px;
  }
`;

const PositionValue = styled(H4)`
  color: ${Colors.ivory};
  font-size: 16px;
  line-height: 24px;
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 22px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${Colors.backgroundGray4};
  background-color: ${Colors.backgroundGray4};
  border-radius: 8px 8px 0 0;
`;

const Tab = styled.button<{ isSelect: boolean }>`
  flex: 1;
  padding: 16px 0;
  background-color: ${(props) =>
    props.isSelect ? Colors.backgroundGray2 : `transparent`};
  color: ${(props) => (props.isSelect ? Colors.ivory : Colors.gray5)};
  border: ${(props) =>
    `1px solid ${props.isSelect ? Colors.backgroundGray2 : "inherit"}`};
  border-bottom-width: 0px;
  font-weight: ${(props) => (props.isSelect ? "bold" : "normal")};
  border-radius: 8px 8px 0 0;
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const MainContainer = styled.div`
  padding: 32px 20px;
`;

const InputContainer = styled.form`
  flex: 1 1 60px;
  padding: 6px 12px 6px 12px;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  border: 1px solid ${Colors.gray4};
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background-color: transparent;
  font-size: 14px;
  line-height: 14px;
  font-weight: bold;
  color: ${Colors.ivory};
  :focus {
    border: none;
    outline: none;
  }
`;

const Unit = styled.span`
  font-size: 13px;
  line-height: 22px;
  color: ${Colors.ivory};
  margin: 0 8px;
`;

const LabelContainer = styled.div`
  position: absolute;
  padding: 0 4px;
  top: -14px;
  left: 8px;
  background-color: ${Colors.backgroundGray2};
`;

const Label = styled(H4)`
  color: ${Colors.gray3};
  font-size: 13px;
  line-height: 22px;
  @media (max-width: 768px) {
  }
`;

const BtnContainer = styled.div`
  button {
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 16px;
    line-height: 24px;
    @media (max-width: 768px) {
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

const BalanceRow = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const BLabel = styled(Label)`
  margin-bottom: 0px;
`;

const BValue = styled(Unit)`
  margin: 0;
`;
